import React, { useMemo } from 'react';
import { ResponsiveSankey } from '@nivo/sankey';
import { ProcessedFunnelData } from '@/lib/analytics/funnel-flow-calculator';
import { ArrowDown } from 'lucide-react';

interface FormConfiguration {
  enable_step_2: boolean;
  reward_enabled: boolean;
  high_rating_email_enabled?: boolean;
  high_rating_email_required?: boolean;
  low_rating_email_enabled?: boolean;
  low_rating_email_required?: boolean;
}

interface ImprovedSankeyVisualizationProps {
  data: ProcessedFunnelData;
  isVertical?: boolean;
  starRatings?: Array<{ rating: number; count: number; percentage: number }>;
  tagScans?: Array<{ tagId: string; tagName: string; count: number; percentage: number }>;
  starThreshold?: number;
  formConfig?: FormConfiguration;
}

// Debug utility for Sankey data validation
const debugSankeyData = (sankeyData: { nodes: any[], links: any[] }) => {
  console.group('Sankey Debug');
  
  // Check for orphaned nodes
  const nodeIds = sankeyData.nodes.map(n => n.id);
  const linkedNodes = new Set([
    ...sankeyData.links.map(l => l.source),
    ...sankeyData.links.map(l => l.target)
  ]);
  const orphans = nodeIds.filter(id => !linkedNodes.has(id));
  
  if (orphans.length > 0) {
    console.warn('Orphaned nodes found:', orphans);
  }
  
  // Check for invalid links
  const invalidLinks = sankeyData.links.filter(link => 
    !nodeIds.includes(link.source) || 
    !nodeIds.includes(link.target) ||
    typeof link.value !== 'number' ||
    link.value <= 0
  );
  
  if (invalidLinks.length > 0) {
    console.error('Invalid links found:', invalidLinks);
  }
  
  console.log('Sankey data summary:', {
    nodes: sankeyData.nodes.length,
    links: sankeyData.links.length,
    totalFlow: sankeyData.links.reduce((sum, l) => sum + l.value, 0)
  });
  
  console.groupEnd();
};

const ImprovedSankeyVisualization: React.FC<ImprovedSankeyVisualizationProps> = ({ 
  data, 
  isVertical = false, 
  starRatings = [],
  tagScans = [],
  starThreshold = 4,
  formConfig
}) => {
  const sankeyData = useMemo(() => {
    const nodes: any[] = [];
    const links: any[] = [];
    
    // Use actual form configuration instead of event-based detection
    const emailEntered = data.branches.high.find(s => s.id === 'email_entered');
    const emailSkipped = data.branches.high.find(s => s.id === 'email_skipped');
    const reviewSubmitted = [...data.branches.high, ...data.branches.low].find(s => s.id === 'review_submitted');
    
    // Use form configuration if available, otherwise fall back to event detection
    const hasRewards = formConfig ? formConfig.reward_enabled : ((emailEntered?.count > 0) || (emailSkipped?.count > 0));
    const hasStep2 = formConfig ? formConfig.enable_step_2 : (hasRewards || (reviewSubmitted?.count > 0));
    
    console.log('🔧 Configuration used:', { 
      source: formConfig ? 'form_config' : 'event_detection',
      hasRewards, 
      hasStep2, 
      formConfig,
      eventData: {
        emailEntered: emailEntered?.count || 0, 
        emailSkipped: emailSkipped?.count || 0, 
        reviewSubmitted: reviewSubmitted?.count || 0 
      },
      allBranches: {
        high: data.branches.high.map(s => s.id),
        low: data.branches.low.map(s => s.id),
        main: data.branches.main.map(s => s.id),
        rewards: data.branches.rewards.map(s => s.id)
      }
    });
    
    // Define depth levels for proper endpoint positioning
    const depthLevels = {
      0: [], // QR scans (entry)
      1: [], // page_loaded
      2: [], // rating_selected, early exits
      3: [], // individual star ratings
      4: [], // email steps, low/high branches
      5: [], // Google redirect, feedback form
      6: [], // review submitted
      7: [], // reward flow start
      8: [], // reward progression
      9: []  // final reward steps
    };
    
    // 1. QR Code entry points (depth 0)
    if (tagScans?.length > 0) {
      tagScans.forEach(tag => {
        if (tag.count > 0) {
          const node = {
            id: `qr_${tag.tagId}`,
            label: `${tag.tagName} (${tag.count})`,
            color: '#6366F1'
          };
          nodes.push(node);
          depthLevels[0].push(node);
        }
      });
    }
    
    // 2. Main entry: page_loaded (depth 1)
    const pageLoadedStep = data.branches.main.find(s => s.id === 'page_loaded');
    if (pageLoadedStep?.count > 0) {
      const node = {
        id: 'page_loaded',
        label: `Page Loaded (${pageLoadedStep.count})`,
        color: pageLoadedStep.color
      };
      nodes.push(node);
      depthLevels[1].push(node);
    }
    
    // 2. Individual star ratings OR early exit (depth 2) 
    // Early exit from page_loaded (depth 2)
    const ratingStep = data.branches.main.find(s => s.id === 'rating_selected');
    if (pageLoadedStep && ratingStep) {
      const earlyExit = pageLoadedStep.count - ratingStep.count;
      if (earlyExit > 0) {
        const exitNode = {
          id: 'early_exit',
          label: `Left Early (${earlyExit})`,
          color: '#9CA3AF'
        };
        nodes.push(exitNode);
        depthLevels[2].push(exitNode);
      }
    }
    
    // Individual star ratings (depth 2 - same level as early_exit)
    if (starRatings?.length > 0) {
      starRatings.forEach(rating => {
        if (rating.count > 0) {
          const node = {
            id: `star_${rating.rating}`,
            label: `${rating.rating}⭐ (${rating.count})`,
            color: rating.rating >= starThreshold ? '#10B981' : '#F59E0B'
          };
          nodes.push(node);
          depthLevels[2].push(node);
        }
      });
    }
    
    
    // 3. Email interaction steps for high ratings (depth 3) - using variables from config detection  
    if (emailEntered?.count > 0) {
      const node = { id: 'email_entered', label: `Email Entered (${emailEntered.count})`, color: emailEntered.color };
      nodes.push(node);
      depthLevels[3].push(node);
    }
    
    if (emailSkipped?.count > 0) {
      const node = { id: 'email_skipped', label: `Email Skipped (${emailSkipped.count})`, color: emailSkipped.color };
      nodes.push(node);
      depthLevels[3].push(node);
    }
    
    // CALCULATE form abandonment like early_exit (no database tracking needed!)
    // Abandonment = ratings that lead to step2 - (email_entered + email_skipped + review_submitted)
    
    if (starRatings && hasStep2) {
      // Determine which ratings lead to step2 based on form configuration  
      let step2RatingEvents = 0;
      let completedStep2Actions = 0;
      
      if (hasRewards) {
        // Config1: reward_enabled=true -> ALL ratings go to step2
        step2RatingEvents = starRatings.reduce((sum, r) => sum + r.count, 0);
        // When rewards enabled, completion can be email actions OR review submissions (both are valid completion paths)
        completedStep2Actions = (emailEntered?.count || 0) + (emailSkipped?.count || 0) + (reviewSubmitted?.count || 0);
      } else if (hasStep2) {
        // Config2: reward_enabled=false, enable_step_2=true -> only LOW ratings go to step2
        step2RatingEvents = starRatings.filter(r => r.rating < starThreshold).reduce((sum, r) => sum + r.count, 0);
        completedStep2Actions = (reviewSubmitted?.count || 0);
      }
      
      // Calculate abandonment
      const calculatedAbandonment = Math.max(0, step2RatingEvents - completedStep2Actions);
      
      console.log('🧮 Calculated abandonment:', {
        hasRewards,
        hasStep2, 
        starThreshold,
        step2RatingEvents,
        completedStep2Actions,
        calculatedAbandonment
      });
      
      if (calculatedAbandonment > 0) {
        const node = { 
          id: 'form_abandoned', 
          label: `Form Abandoned (${calculatedAbandonment})`, 
          color: '#DC2626' 
        };
        nodes.push(node);
        depthLevels[3].push(node);
      }
    }

    // 4. Google redirect for high ratings (depth 4)
    const googleRedirect = data.branches.high.find(s => s.id === 'redirected_to_google');
    if (googleRedirect?.count > 0) {
      const node = { id: 'redirected_to_google', label: `To Google (${googleRedirect.count})`, color: googleRedirect.color };
      nodes.push(node);
      depthLevels[4].push(node);
    }
    
    // Note: Removed feedback_form_shown as it's redundant - it's always shown if reached
    
    // 4. Review submitted (depth 4) - using variable from config detection  
    if (reviewSubmitted?.count > 0) {
      const node = { id: 'review_submitted', label: `Review Submitted (${reviewSubmitted.count})`, color: reviewSubmitted.color };
      nodes.push(node);
      depthLevels[4].push(node);
    }
    
    // 5. Reward offered (depth 5)
    const rewardOffered = data.branches.rewards.find(s => s.id === 'reward_offered');
    if (rewardOffered?.count > 0) {
      const node = { id: 'reward_offered', label: `Reward Offered (${rewardOffered.count})`, color: rewardOffered.color };
      nodes.push(node);
      depthLevels[5].push(node);
    }
    
    // 6. Final reward steps (depth 6)
    const rewardRedeemed = data.branches.rewards.find(s => s.id === 'reward_redeemed');
    if (rewardRedeemed?.count > 0) {
      const node = { id: 'reward_redeemed', label: `Redeemed (${rewardRedeemed.count})`, color: rewardRedeemed.color };
      nodes.push(node);
      depthLevels[6].push(node);
    }
    
    // Add invisible "endpoint spacer" nodes to force different ending positions
    // These create the proper depth structure that Sankey will respect
    
    // Depth 2 endpoint (early exits)  
    nodes.push({ id: 'endpoint_depth_2', label: '', color: 'transparent' });
    depthLevels[2].push(nodes[nodes.length - 1]);
    
    // Depth 3 endpoint (form abandonment)
    nodes.push({ id: 'endpoint_depth_3', label: '', color: 'transparent' });
    depthLevels[3].push(nodes[nodes.length - 1]);
    
    // Depth 4 endpoint (review submitted, Google redirect)
    nodes.push({ id: 'endpoint_depth_4', label: '', color: 'transparent' });
    depthLevels[4].push(nodes[nodes.length - 1]);
    
    // Depth 6 endpoint (complete reward flow)
    nodes.push({ id: 'endpoint_depth_6', label: '', color: 'transparent' });
    depthLevels[6].push(nodes[nodes.length - 1]);
    
    // Create links with proper flow (only if both source and target nodes exist)
    // QR to page_loaded
    if (tagScans?.length > 0 && pageLoadedStep && nodes.some(n => n.id === 'page_loaded')) {
      tagScans.forEach(tag => {
        if (tag.count > 0 && nodes.some(n => n.id === `qr_${tag.tagId}`)) {
          links.push({
            source: `qr_${tag.tagId}`,
            target: 'page_loaded',
            value: tag.count,
            color: '#6366F1'
          });
        }
      });
    }
    
    // Page loaded to individual star ratings OR early exit  
    if (pageLoadedStep && nodes.some(n => n.id === 'page_loaded')) {
      // Connect directly to each star rating
      if (starRatings?.length > 0) {
        starRatings.forEach(rating => {
          if (rating.count > 0 && nodes.some(n => n.id === `star_${rating.rating}`)) {
            links.push({
              source: 'page_loaded',
              target: `star_${rating.rating}`,
              value: rating.count,
              color: rating.rating >= starThreshold ? '#10B981' : '#F59E0B'
            });
          }
        });
      }
      
      const earlyExit = pageLoadedStep.count - (ratingStep?.count || 0);
      if (earlyExit > 0 && nodes.some(n => n.id === 'early_exit')) {
        // Connect early exit to depth 2 endpoint
        links.push({
          source: 'page_loaded',
          target: 'early_exit',
          value: earlyExit,
          color: '#9CA3AF'
        });
        links.push({
          source: 'early_exit',
          target: 'endpoint_depth_2',
          value: earlyExit,
          color: '#9CA3AF',
          opacity: 0
        });
      }
    }
    
    // Stars now connect directly from page_loaded (removed intermediate rating_selected)
    
    // High stars to step2 actions (MUTUALLY EXCLUSIVE)
    if (starRatings && hasStep2) {
      const highStarRatings = starRatings.filter(r => r.rating >= starThreshold && r.count > 0);
      const totalHighRatingCount = highStarRatings.reduce((sum, r) => sum + r.count, 0);
      
      if (totalHighRatingCount > 0) {
        // Calculate total step2 actions for high ratings (mutually exclusive)
        const highStep2Actions = (emailEntered?.count || 0) + (emailSkipped?.count || 0);
        
        // For rewards enabled: use email_entered + email_skipped
        // For rewards disabled: use review_submitted (but from high ratings only)
        let highReviewSubmitted = 0;
        if (!hasRewards && reviewSubmitted) {
          // Estimate high rating portion of review_submitted
          highReviewSubmitted = Math.round(reviewSubmitted.count * (totalHighRatingCount / (totalHighRatingCount + (starRatings.filter(r => r.rating < starThreshold).reduce((sum, r) => sum + r.count, 0) || 1))));
        }
        
        const totalStep2Count = hasRewards ? highStep2Actions : highReviewSubmitted;
        
        console.log('📊 High rating step2 distribution:', { 
          totalHighRatingCount, 
          hasRewards,
          emailEntered: emailEntered?.count || 0,
          emailSkipped: emailSkipped?.count || 0,
          highReviewSubmitted,
          totalStep2Count,
          starThreshold 
        });
        
        if (hasRewards && totalStep2Count > 0) {
          // ONLY when rewards enabled do high ratings go to step2
          highStarRatings.forEach(rating => {
            // Distribute this rating's count across step2 actions proportionally
            const ratingProportion = rating.count / totalHighRatingCount;
            
            // Rewards enabled: email_entered, email_skipped, form_abandoned
            if (emailEntered && emailEntered.count > 0) {
              const flowValue = Math.round(ratingProportion * emailEntered.count);
              if (flowValue > 0) {
                links.push({
                  source: `star_${rating.rating}`,
                  target: 'email_entered',
                  value: flowValue,
                  color: emailEntered.color
                });
              }
            }
            
            if (emailSkipped && emailSkipped.count > 0) {
              const flowValue = Math.round(ratingProportion * emailSkipped.count);
              if (flowValue > 0) {
                links.push({
                  source: `star_${rating.rating}`,
                  target: 'email_skipped',
                  value: flowValue,
                  color: emailSkipped.color
                });
              }
            }
            
            // Abandonment handled separately after all star rating loops
          });
        } else if (!hasRewards && googleRedirect && googleRedirect.count > 0) {
          // Rewards disabled: high ratings go DIRECTLY to Google (skip step2)
          highStarRatings.forEach(rating => {
            const flowValue = rating.count;
            if (flowValue > 0) {
              links.push({
                source: `star_${rating.rating}`,
                target: 'redirected_to_google',
                value: flowValue,
                color: '#10B981'
              });
            }
          });
        }
      }
    }

    // Email interaction steps to Google redirect
    if (googleRedirect && googleRedirect.count > 0) {
      // Email entered to Google
      if (emailEntered && emailEntered.count > 0) {
        links.push({
          source: 'email_entered',
          target: 'redirected_to_google',
          value: emailEntered.count,
          color: emailEntered.color
        });
      }
      
      // Email skipped to Google  
      if (emailSkipped && emailSkipped.count > 0) {
        links.push({
          source: 'email_skipped',
          target: 'redirected_to_google',
          value: emailSkipped.count,
          color: emailSkipped.color
        });
      }
    }
    
    // Note: Removed high stars to feedback_form_shown - redundant intermediate step
    
    // Low stars logic depends on step2 availability
    if (starRatings) {
      const lowStarRatings = starRatings.filter(r => r.rating < starThreshold && r.count > 0);
      const totalLowRatingCount = lowStarRatings.reduce((sum, r) => sum + r.count, 0);
      const totalHighRatingCount = starRatings.filter(r => r.rating >= starThreshold).reduce((sum, r) => sum + r.count, 0);
      
      if (totalLowRatingCount > 0) {
        if (hasStep2 && reviewSubmitted) {
          // Step2 enabled: low ratings can use review_submitted or abandon
          let lowReviewSubmitted = reviewSubmitted.count;
          if (hasRewards) {
            // When rewards enabled, review_submitted is mostly from low ratings (high ratings use email actions)
            lowReviewSubmitted = reviewSubmitted.count;
          } else {
            // When rewards disabled, review_submitted is ONLY from low ratings (high ratings skip step2)
            lowReviewSubmitted = reviewSubmitted.count;
          }
          
          const lowStep2Count = lowReviewSubmitted;
          
          console.log('📊 Low rating step2 distribution:', { 
            totalLowRatingCount, 
            hasRewards,
            hasStep2,
            lowReviewSubmitted,
            lowStep2Count,
            starThreshold 
          });
          
          if (lowStep2Count > 0) {
            lowStarRatings.forEach(rating => {
              // Each star should have total outflow equal to its count
              // So we need to split rating.count between review_submitted and form_abandoned
              const ratingProportion = rating.count / totalLowRatingCount;
              
              // Calculate review_submitted flow for this rating
              const flowToReview = Math.round(ratingProportion * lowReviewSubmitted);
              
              // The remaining flow should go to abandonment (will be handled in abandonment section)
              // Total outflow = flowToReview + flowToAbandonment should equal rating.count
              
              // Connect to review_submitted
              if (flowToReview > 0) {
                links.push({
                  source: `star_${rating.rating}`,
                  target: 'review_submitted',
                  value: flowToReview,
                  color: '#F59E0B'
                });
              }
              
              // Abandonment handled separately after all star rating loops
            });
          }
        } else {
          // Step2 disabled: low ratings just END (no further visualization needed)
          console.log('📊 Low ratings with no step2 - END state:', { 
            totalLowRatingCount,
            hasStep2,
            starThreshold,
            message: 'Low ratings end here - no visualization nodes needed' 
          });
          // No links created - low ratings simply end
        }
      }
    }
    
    // CALCULATED Form Abandonment Links (separate from star rating loops)
    const calculatedAbandonmentNode = nodes.find(n => n.id === 'form_abandoned');
    if (calculatedAbandonmentNode && starRatings?.length > 0) {
      const abandonmentCount = parseInt(calculatedAbandonmentNode.label.match(/\((\d+)\)/)?.[1] || '0');
      
      if (abandonmentCount > 0) {
        if (hasRewards) {
          // Config1: Rewards enabled - ALL stars should go to email step
          // If no email actions occurred, then ALL ratings abandoned
          const totalEmailActions = (emailEntered?.count || 0) + (emailSkipped?.count || 0);
          const totalReviewSubmitted = reviewSubmitted?.count || 0;
          
          console.log('🔍 Rewards enabled abandonment:', {
            totalEmailActions,
            totalReviewSubmitted,
            abandonmentCount,
            starRatings: starRatings.map(r => ({ rating: r.rating, count: r.count }))
          });
          
          // Simple approach: if total completions = 0, all ratings abandon completely
          if (totalEmailActions === 0 && totalReviewSubmitted === 0) {
            starRatings.forEach(rating => {
              if (rating.count > 0) {
                links.push({
                  source: `star_${rating.rating}`,
                  target: 'form_abandoned',
                  value: rating.count,
                  color: '#DC2626'
                });
                console.log(`⭐ ${rating.rating}⭐ → abandoned (${rating.count})`);
              }
            });
          } else {
            // Complex proportional distribution when some completions exist
            starRatings.forEach(rating => {
              if (rating.count > 0) {
                let estimatedCompletions = 0;
                
                if (rating.rating >= starThreshold) {
                  // High rating - gets email actions 
                  const highRatingCount = starRatings.filter(r => r.rating >= starThreshold).reduce((sum, r) => sum + r.count, 0);
                  if (highRatingCount > 0) {
                    estimatedCompletions = Math.round((rating.count / highRatingCount) * totalEmailActions);
                  }
                } else {
                  // Low rating - gets review_submitted 
                  const lowRatingCount = starRatings.filter(r => r.rating < starThreshold).reduce((sum, r) => sum + r.count, 0);
                  if (lowRatingCount > 0) {
                    estimatedCompletions = Math.round((rating.count / lowRatingCount) * totalReviewSubmitted);
                  }
                }
                
                const thisRatingAbandonment = Math.max(0, rating.count - estimatedCompletions);
                
                if (thisRatingAbandonment > 0) {
                  links.push({
                    source: `star_${rating.rating}`,
                    target: 'form_abandoned',
                    value: thisRatingAbandonment,
                    color: '#DC2626'
                  });
                  console.log(`⭐ ${rating.rating}⭐ → abandoned (${thisRatingAbandonment})`);
                }
              }
            });
          }
        } else if (hasStep2) {
          // Config2: Rewards disabled, step2 enabled - only LOW ratings lead to step2, so only LOW can abandon
          const lowRatings = starRatings.filter(r => r.rating < starThreshold);
          const lowRatingCount = lowRatings.reduce((sum, r) => sum + r.count, 0);
          const reviewSubmittedNode = nodes.find(n => n.id === 'review_submitted');
          const totalReviewSubmitted = reviewSubmittedNode ? parseInt(reviewSubmittedNode.label.match(/\((\d+)\)/)?.[1] || '0') : 0;
          
          if (lowRatingCount > 0) {
            lowRatings.forEach(rating => {
              if (rating.count > 0) {
                // Calculate how much of this rating went to review_submitted
                const ratingProportion = rating.count / lowRatingCount;
                const flowToReview = Math.round(ratingProportion * totalReviewSubmitted);
                
                // The remainder goes to abandonment 
                const flowToAbandonment = rating.count - flowToReview;
                
                if (flowToAbandonment > 0) {
                  links.push({
                    source: `star_${rating.rating}`,
                    target: 'form_abandoned',
                    value: flowToAbandonment,
                    color: '#DC2626'
                  });
                }
              }
            });
          }
        }
        // Config3: step2 disabled - no abandonment possible
      }
    }
    
    // Google redirect to rewards (high rating customers get rewarded)
    if (googleRedirect && rewardOffered && 
        nodes.some(n => n.id === 'redirected_to_google') && 
        nodes.some(n => n.id === 'reward_offered')) {
      links.push({
        source: 'redirected_to_google',
        target: 'reward_offered',
        value: rewardOffered.count,
        color: rewardOffered.color
      });
      
      // Users redirected to Google but don't take reward
      const noRewardUsers = googleRedirect.count - rewardOffered.count;
      if (noRewardUsers > 0) {
        links.push({
          source: 'redirected_to_google',
          target: 'endpoint_depth_4',
          value: noRewardUsers,
          color: '#9CA3AF',
          opacity: 0
        });
      }
    } else if (googleRedirect && nodes.some(n => n.id === 'redirected_to_google')) {
      // No reward system - Google redirect ends here
      links.push({
        source: 'redirected_to_google',
        target: 'endpoint_depth_4',
        value: googleRedirect.count,
        color: '#9CA3AF',
        opacity: 0
      });
    }
    
    // Note: Removed feedback_form_shown to review_submitted - now direct from low stars
    
    // Review submitted should END (low ratings don't go to Google)
    // Low ratings submit internal feedback and that's the end of their journey
    if (reviewSubmitted && nodes.some(n => n.id === 'review_submitted')) {
      // Connect to depth 5 endpoint (review submitted but no rewards)
      links.push({
        source: 'review_submitted',
        target: 'endpoint_depth_4',
        value: reviewSubmitted.count,
        color: '#9CA3AF',
        opacity: 0
      });
    }
    
    // Reward to final step (only if both nodes exist)
    if (rewardOffered && nodes.some(n => n.id === 'reward_offered')) {
      if (rewardRedeemed && nodes.some(n => n.id === 'reward_redeemed')) {
        links.push({
          source: 'reward_offered',
          target: 'reward_redeemed',
          value: rewardRedeemed.count,
          color: rewardRedeemed.color
        });
        
        // Connect final reward to depth 8 endpoint
        links.push({
          source: 'reward_redeemed',
          target: 'endpoint_depth_6',
          value: rewardRedeemed.count,
          color: '#9CA3AF',
          opacity: 0
        });
        
        // Users who get reward but don't redeem (also end at depth 8 since they reached reward stage)
        const unredeemed = rewardOffered.count - rewardRedeemed.count;
        if (unredeemed > 0) {
          links.push({
            source: 'reward_offered',
            target: 'endpoint_depth_6',
            value: unredeemed,
            color: '#9CA3AF',
            opacity: 0
          });
        }
      } else {
        // Reward offered but no redemption data (end at depth 8)
        links.push({
          source: 'reward_offered',
          target: 'endpoint_depth_6',
          value: rewardOffered.count,
          color: '#9CA3AF',
          opacity: 0
        });
      }
    }
    
    // Connect calculated abandonment to endpoint (depth 3)
    // reuse calculatedAbandonmentNode from above
    const abandonmentNodeForEndpoint = nodes.find(n => n.id === 'form_abandoned');
    if (abandonmentNodeForEndpoint && nodes.some(n => n.id === 'endpoint_depth_3')) {
      const abandonmentCount = parseInt(abandonmentNodeForEndpoint.label.match(/\((\d+)\)/)?.[1] || '0');
      if (abandonmentCount > 0) {
        links.push({
          source: 'form_abandoned',
          target: 'endpoint_depth_3',
          value: abandonmentCount,
          color: '#9CA3AF',
          opacity: 0
        });
      }
    }
    
    return { nodes, links };
  }, [data, starRatings, tagScans, starThreshold, formConfig]);
  // Always show Sankey when we have data
  const showSankey = sankeyData.nodes.length >= 2 && sankeyData.links.length >= 1;
  
  return (
    <div className="w-full">
      {/* Add global styles for invisible spacer links */}
      <style>
        {`
          path[fill*=".endpoint_depth"] {
            opacity: 0 !important;
          }
        `}
      </style>
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-foreground">
              Customer Journey Flow
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {showSankey 
                ? `Interactive ${isVertical ? 'vertical' : 'horizontal'} flow diagram showing customer progression` 
                : 'Simplified flow view (building comprehensive data for advanced diagram)'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              showSankey
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
            }`}>
              {showSankey ? '🎯 Advanced Flow' : '📊 Basic Flow'}
            </div>
          </div>
        </div>
      </div>
      
      {/* Key Metrics */}
      <div className="mb-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                {data.metrics.totalSessions.toLocaleString()}
              </div>
              <div className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Sessions</div>
            </div>
            <div className="p-2 bg-blue-200 dark:bg-blue-800 rounded-lg">
              <span className="text-lg">👥</span>
            </div>
          </div>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-xl border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                {data.metrics.overallConversion.toFixed(1)}%
              </div>
              <div className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Overall Conversion</div>
            </div>
            <div className="p-2 bg-emerald-200 dark:bg-emerald-800 rounded-lg">
              <span className="text-lg">🎯</span>
            </div>
          </div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl border border-amber-200 dark:border-amber-800">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                {data.metrics.highRatingRate.toFixed(1)}%
              </div>
              <div className="text-sm font-medium text-amber-600 dark:text-amber-400">High Rating Rate</div>
            </div>
            <div className="p-2 bg-amber-200 dark:bg-amber-800 rounded-lg">
              <span className="text-lg">⭐</span>
            </div>
          </div>
        </div>
        <div className="bg-primary/5 p-4 rounded-xl border border-primary/20">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-primary">
                {data.metrics.rewardRedemptionRate.toFixed(1)}%
              </div>
              <div className="text-sm font-medium text-muted-foreground">Reward Redemption</div>
            </div>
            <div className="p-2 bg-primary/10 rounded-lg">
              <span className="text-lg">🎁</span>
            </div>
          </div>
        </div>
      </div>

      {showSankey ? (
        // Professional Sankey Diagram
        <div className="bg-card rounded-xl border p-6">
          <div style={{ height: isVertical ? '700px' : '500px' }}>
            <ResponsiveSankey
              data={sankeyData}
              margin={isVertical 
                ? { top: 60, right: 120, bottom: 60, left: 120 }
                : { top: 40, right: 200, bottom: 40, left: 80 }
              }
              layout={isVertical ? 'vertical' : 'horizontal'}
              align="justify"
              colors={{ scheme: 'purple_orange' }}
              nodeOpacity={(node) => {
                if (!node || !node.id) return 0.9;
                return node.id.startsWith('endpoint_') ? 0 : 0.9;
              }}
              nodeHoverOthersOpacity={0.2}
              nodeThickness={2}
              nodeInnerPadding={1}
              nodeSpacing={isVertical ? 32 : 24}
              nodeBorderWidth={0}
              linkOpacity={1}
              linkHoverOthersOpacity={1}
              linkHoverOpacity={1}
              linkContract={0}
              enableLinkGradient={true}
              labelPosition="outside"
              labelOrientation="horizontal"
              labelPadding={20}
              labelTextColor={(node) => {
                if (!node || !node.id) {
                  return { from: 'color', modifiers: [['darker', 1.2]] };
                }
                return node.id.startsWith('endpoint_') ? 'transparent' : {
                  from: 'color',
                  modifiers: [['darker', 1.2]]
                };
              }}
              nodeTooltip={(node) => {
                if (!node || !node.id || node.id.startsWith('endpoint_')) {
                  return null;
                }
                return (
                  <div className="bg-popover p-3 rounded-lg shadow-lg border">
                    <div className="font-semibold text-popover-foreground">{node.label}</div>
                    <div className="text-sm text-muted-foreground">Total Flow: {node.value}</div>
                  </div>
                );
              }}
              animate={true}
              motionStiffness={90}
              motionDamping={12}
              isInteractive={true}
              linkTooltip={(link) => {
                // Skip tooltips for invisible links or links with missing data
                if (!link || link.opacity === 0 || !link.source || !link.target || !link.source.label || !link.target.label) {
                  return null;
                }

                return (
                  <div className="bg-popover p-3 rounded-lg shadow-lg border">
                    <div className="font-semibold text-popover-foreground">
                      {link.source.label} → {link.target.label}
                    </div>
                    <div className="text-sm text-muted-foreground">Flow: {link.value} users</div>
                  </div>
                );
              }}
            />
          </div>
        </div>
      ) : (
        // Simple message when no Sankey data available
        <div className="bg-card rounded-xl border p-6">
          <div className="text-center py-8">
            <p className="text-muted-foreground">No data available for visualization.</p>
          </div>
        </div>
      )}

      {/* Enhanced Flow Analysis */}
      <div className="mt-8 bg-muted rounded-xl p-6 border">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-lg text-foreground">📊 Flow Analysis Summary</h4>
          <div className={`px-3 py-1 rounded-full text-xs font-bold ${
            data.metrics.overallConversion > 50
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : data.metrics.overallConversion > 20
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
          }`}>
            {data.metrics.overallConversion > 50 ? '✅ Excellent Performance' :
             data.metrics.overallConversion > 20 ? '⚠️ Good Performance' : '🔄 Needs Improvement'}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-card rounded-lg p-4 border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <span className="text-lg">{showSankey ? '🎯' : '📈'}</span>
              </div>
              <div>
                <div className="font-semibold text-foreground">Visualization</div>
                <div className="text-sm text-muted-foreground">
                  {showSankey ? `${isVertical ? 'Vertical ' : 'Horizontal '}Sankey Diagram` : 'Enhanced Linear Flow'}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg p-4 border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <span className="text-lg">📊</span>
              </div>
              <div>
                <div className="font-semibold text-foreground">Data Complexity</div>
                <div className="text-sm text-muted-foreground">
                  {showSankey ? `${sankeyData.nodes.length} nodes, ${sankeyData.links.length} connections` : 'No data available'}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg p-4 border">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${
                data.metrics.overallConversion > 50 ? 'bg-emerald-100 dark:bg-emerald-900/30'
                : data.metrics.overallConversion > 20 ? 'bg-amber-100 dark:bg-amber-900/30'
                : 'bg-red-100 dark:bg-red-900/30'
              }`}>
                <span className="text-lg">
                  {data.metrics.overallConversion > 50 ? '🚀' : data.metrics.overallConversion > 20 ? '📈' : '🔧'}
                </span>
              </div>
              <div>
                <div className="font-semibold text-foreground">Health Score</div>
                <div className={`text-sm font-medium ${
                  data.metrics.overallConversion > 50 ? 'text-emerald-600 dark:text-emerald-400'
                  : data.metrics.overallConversion > 20 ? 'text-amber-600 dark:text-amber-400'
                  : 'text-red-600 dark:text-red-400'
                }`}>
                  {data.metrics.overallConversion.toFixed(1)}% overall conversion
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImprovedSankeyVisualization;